import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Cart upsell (snippets/vv-cart-upsell.liquid): one click adds the offered variant through /cart/add.js,
 * the cart sections re-render through the theme's CartLinesUpdateEvent and the block disappears.
 * On an error the button goes back to its original state and a short message shows.
 */
const BUTTON_SELECTOR = '[data-vv-upsell-add]';
let busy = false;

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest(BUTTON_SELECTOR) : null;
  if (!(target instanceof HTMLButtonElement)) return;
  event.preventDefault();
  if (busy || target.disabled) return;
  addToCart(target);
});

/**
 * @param {HTMLButtonElement} button
 */
async function addToCart(button) {
  const variantId = Number(button.dataset.variantId);
  if (!variantId) return;

  const wrapper = button.closest('[data-vv-upsell]');
  const errorNode = wrapper?.querySelector('.vv-up__err');
  const originalText = button.textContent;

  busy = true;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  if (errorNode instanceof HTMLElement) errorNode.hidden = true;

  const sectionIds = [];
  for (const component of document.querySelectorAll('cart-items-component')) {
    if (component instanceof HTMLElement && component.dataset.sectionId) sectionIds.push(component.dataset.sectionId);
  }

  const routes = window.Theme?.routes ?? {};
  const addUrl = routes.cart_add_url || '/cart/add.js';
  const cartUrl = `${routes.cart_url || '/cart'}.js`;

  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || `Add to cart failed (${response.status})`);
    }

    const cartResponse = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) throw new Error(`Cart fetch failed (${cartResponse.status})`);
    const cart = await cartResponse.json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: data.sections,
        source: 'vv-cart-upsell',
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.textContent = originalText;
    if (errorNode instanceof HTMLElement) errorNode.hidden = false;
    console.warn('[vv-cart-upsell]', error);
  } finally {
    busy = false;
  }
}

/**
 * A necklace sold as a set (snippets/vv-set.liquid, part 'cart'): the cart holds one included product for each
 * necklace of a set, no more and no less. The product page adds it with the necklace; here, in the drawer and on
 * /cart, the count is put right after the cart was changed (a necklace removed, a quantity changed) and when the
 * page loads. The line is set through /cart/update.js, then the page is loaded again once so every part of the
 * cart shows the same thing.
 */
const SET_RELOAD_KEY = 'vv-set-reload';
let setBusy = false;
let setTimer = 0;

/**
 * @returns {{ id: number, triggers: number[] } | null}
 */
function readSet() {
  const node = document.querySelector('script[data-vv-set-cart]');
  if (!node) return null;
  try {
    const set = JSON.parse(node.textContent || 'null');
    return set && set.id && Array.isArray(set.triggers) ? set : null;
  } catch (error) {
    return null;
  }
}

async function syncSet() {
  const set = readSet();
  if (!set || setBusy) return;
  setBusy = true;
  try {
    const routes = window.Theme?.routes ?? {};
    const cartRoot = routes.cart_url || '/cart';
    const cartResponse = await fetch(`${cartRoot}.js`, { headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) return;
    const cart = await cartResponse.json();
    let want = 0;
    let have = 0;
    for (const item of cart.items) {
      if (set.triggers.includes(item.product_id)) want += item.quantity;
      if (item.variant_id === set.id) have += item.quantity;
    }
    if (want === have) {
      sessionStorage.removeItem(SET_RELOAD_KEY);
      return;
    }
    /* the same correction twice in a row means it does not hold: leave the cart as it is rather than reload again */
    const wanted = `${want}:${have}`;
    if (sessionStorage.getItem(SET_RELOAD_KEY) === wanted) return;
    const response = await fetch(routes.cart_update_url ? `${routes.cart_update_url}.js` : `${cartRoot}/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: { [set.id]: want } }),
    });
    if (!response.ok) return;
    sessionStorage.setItem(SET_RELOAD_KEY, wanted);
    window.location.reload();
  } catch (error) {
    console.warn('[vv-set]', error);
  } finally {
    setBusy = false;
  }
}

if (readSet()) {
  syncSet();
  const observer = new MutationObserver(() => {
    window.clearTimeout(setTimer);
    setTimer = window.setTimeout(syncSet, 600);
  });
  for (const component of document.querySelectorAll('cart-items-component')) {
    observer.observe(component, { childList: true, subtree: true });
  }
}
